import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaArrowRight, FaChevronLeft, FaChevronRight,
  FaStar, FaPhone
} from 'react-icons/fa6';

// ---- Logos ----
import AZ from '../../assets/logos/LOG.png';
import AZ2 from '../../assets/logos/az.png';

// ---- Images de fond ----
import bg1 from '../../assets/images/banner/bg1.jpeg';
import bg2 from '../../assets/images/banner/bg2.jpeg';
import bg3 from '../../assets/images/banner/bg3.jpeg';
import bg6 from '../../assets/images/banner/bg6.jpeg';

// ==================== PALETTE LUXE ====================
const colors = {
  darkBg: '#0a0a0c',
  gold: '#C69C6D',
  goldHover: '#D4AF37',
  goldLight: '#E8D5B5',
  textLight: '#F5F0EB',
  textMuted: '#B7A99A',
  glassBg: 'rgba(20, 20, 25, 0.55)',
  glassBorder: 'rgba(198, 156, 109, 0.25)',
  shadowGold: '0 8px 32px rgba(198, 156, 109, 0.35)',
};

// ==================== PARTICULES OPTIMISÉES ====================
const EnergyParticles = ({ count = 40 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0, height = 0;
    const resize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        const rect = parent.getBoundingClientRect();
        width = rect.width || window.innerWidth;
        height = rect.height || window.innerHeight;
      } else {
        // fallback
        width = window.innerWidth;
        height = window.innerHeight;
      }
      canvas.width = width;
      canvas.height = height;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.8,
      vy: (Math.random() - 0.5) * 0.8,
      radius: Math.random() * 2.5 + 0.5,
      baseAlpha: Math.random() * 0.3 + 0.15,
    }));

    let frameId;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        const alpha = p.baseAlpha + Math.sin(Date.now() * 0.0015 + p.x * 0.01) * 0.15;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(45, 70%, 75%, ${alpha})`;
        ctx.fill();

        // Lueur
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius * 4);
        grad.addColorStop(0, `hsla(45, 70%, 75%, ${alpha * 0.4})`);
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * 4, 0, Math.PI * 2);
        ctx.fill();
      });

      // Connexions
      ctx.strokeStyle = 'rgba(198,156,109,0.06)';
      ctx.lineWidth = 0.5;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.globalAlpha = ((120 - dist) / 120) * 0.2;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;

      frameId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        pointerEvents: 'none',
        width: '100%',
        height: '100%',
      }}
    />
  );
};

// ==================== DONNÉES DES SLIDES ====================
const slides = [
  {
    bg: bg1,
    logo: AZ,
    title: "L'art du service sur mesure.",
    subtitle: "Votre signature de prestige.",
    buttons: [
      { label: "Nos Services", icon: <FaStar />, to: "/Services", primary: true },
      { label: "Contactez-nous", icon: <FaPhone />, to: "/Contact", primary: false },
    ],
  },
  {
    bg: bg2,
    logo: AZ2,
    title: "Parce que vous méritez le meilleur, simplement.",
    subtitle: null,
    buttons: [
      { label: "Nos Services", icon: <FaArrowRight />, to: "/Services", primary: true },
    ],
  },
  {
    bg: bg3,
    logo: AZ,
    title: "Conciergerie Privée",
    subtitle: "Un service discret, raffiné, personnalisé.",
    description:
      "Notre Conciergerie Privée vous offre un accompagnement exclusif et sur mesure.",
    buttons: [
      { label: "Plus d'infos", icon: <FaStar />, to: "/Conciergerie-privee", primary: true },
    ],
  },
  {
    bg: bg6,
    logo: AZ,
    title: "Location de véhicules",
    subtitle: "Excellence et prestige sur roues.",
    description: "Découvrez notre flotte de véhicules haut de gamme.",
    buttons: [
      { label: "Découvrir", icon: <FaArrowRight />, to: "/RentCars", primary: true },
    ],
  },
];

// ==================== COMPOSANT PRINCIPAL ====================
const Banner = () => {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(0);
  const intervalRef = useRef(null); // on stocke un number
  const containerRef = useRef(null);

  const goTo = useCallback((index) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  }, [current]); // dépend de current

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  // Auto-play
  useEffect(() => {
    if (isPaused) return;
    intervalRef.current = window.setInterval(next, 6000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused, next]);

  // Pause si onglet caché
  useEffect(() => {
    const handleVisibility = () => {
      setIsPaused(document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Swipe tactile
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let startX = 0;
    let isSwiping = false;

    const onTouchStart = (e) => {
      startX = e.changedTouches[0].screenX;
      isSwiping = true;
    };
    const onTouchEnd = (e) => {
      if (!isSwiping) return;
      const endX = e.changedTouches[0].screenX;
      const diff = startX - endX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) next();
        else prev();
      }
      isSwiping = false;
    };
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [next, prev]);

  // Variantes d'animation
  const slideVariants = {
    enter: (dir) => ({
      opacity: 0,
      scale: 0.95,
      filter: 'blur(6px)',
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
    }),
    center: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
    },
    exit: (dir) => ({
      opacity: 0,
      scale: 1.05,
      filter: 'blur(4px)',
      transition: { duration: 0.5, ease: 'easeIn' },
    }),
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.12,
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', damping: 12, stiffness: 100 },
    },
  };

  // Styles mémorisés
  const btnPrimaryStyle = useMemo(() => ({
    background: `linear-gradient(135deg, ${colors.gold}, ${colors.goldHover})`,
    color: '#1a1a1a',
    border: 'none',
    padding: '0.85rem 2.4rem',
    borderRadius: '60px',
    fontWeight: 600,
    fontSize: 'clamp(0.9rem, 1.8vw, 1.05rem)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    letterSpacing: '0.03em',
    boxShadow: colors.shadowGold,
    textDecoration: 'none',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    position: 'relative',
    overflow: 'hidden',
  }), []);

  const btnOutlineStyle = useMemo(() => ({
    background: 'rgba(20,20,25,0.4)',
    backdropFilter: 'blur(8px)',
    color: colors.textLight,
    border: `2px solid ${colors.gold}`,
    padding: '0.85rem 2.4rem',
    borderRadius: '60px',
    fontWeight: 500,
    fontSize: 'clamp(0.9rem, 1.8vw, 1.05rem)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    textDecoration: 'none',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  }), []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        minHeight: '600px',
        overflow: 'hidden',
        background: colors.darkBg,
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="region"
      aria-label="Carrousel de présentation"
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={current}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${slides[current].bg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 30% 50%, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.75) 100%)',
              zIndex: 2,
            }}
          />
          <EnergyParticles count={35} />

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{
              position: 'relative',
              zIndex: 3,
              textAlign: 'center',
              padding: '0 24px',
              maxWidth: '900px',
              width: '100%',
            }}
          >
            {slides[current].logo && (
              <motion.img
                src={slides[current].logo}
                alt="Logo"
                variants={itemVariants}
                style={{
                  width: 'clamp(140px, 30vw, 260px)',
                  margin: '0 auto 1.8rem',
                  filter: 'drop-shadow(0 0 30px rgba(198,156,109,0.5))',
                }}
              />
            )}
            <motion.h2
              variants={itemVariants}
              style={{
                fontSize: 'clamp(2rem, 5vw, 3.6rem)',
                fontWeight: 300,
                background: `linear-gradient(135deg, #ffffff 0%, ${colors.goldLight} 80%, ${colors.gold} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem',
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              {slides[current].title}
            </motion.h2>
            {slides[current].subtitle && (
              <motion.p
                variants={itemVariants}
                style={{
                  color: colors.textLight,
                  fontSize: 'clamp(1rem, 2.2vw, 1.3rem)',
                  marginBottom: '0.8rem',
                  fontWeight: 300,
                  letterSpacing: '0.05em',
                }}
              >
                {slides[current].subtitle}
              </motion.p>
            )}
            {slides[current].description && (
              <motion.p
                variants={itemVariants}
                style={{
                  color: colors.textMuted,
                  fontSize: 'clamp(0.9rem, 1.6vw, 1rem)',
                  marginBottom: '2rem',
                  lineHeight: 1.7,
                  maxWidth: '600px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                {slides[current].description}
              </motion.p>
            )}
            <motion.div
              variants={itemVariants}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '1rem',
              }}
            >
              {slides[current].buttons.map((btn, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.06, y: -2 }}
                  whileTap={{ scale: 0.94 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                >
                  <Link
                    to={btn.to}
                    style={btn.primary ? btnPrimaryStyle : btnOutlineStyle}
                    className="banner-btn"
                  >
                    {btn.icon}
                    {btn.label}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Flèches */}
      <motion.button
        onClick={prev}
        whileHover={{ scale: 1.15, background: colors.gold, color: '#1a1a1a' }}
        whileTap={{ scale: 0.9 }}
        style={{
          position: 'absolute',
          left: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          background: colors.glassBg,
          backdropFilter: 'blur(12px)',
          border: `1px solid ${colors.glassBorder}`,
          borderRadius: '50%',
          width: '52px',
          height: '52px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.gold,
          cursor: 'pointer',
          transition: 'all 0.3s',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}
        aria-label="Slide précédent"
      >
        <FaChevronLeft size={22} />
      </motion.button>

      <motion.button
        onClick={next}
        whileHover={{ scale: 1.15, background: colors.gold, color: '#1a1a1a' }}
        whileTap={{ scale: 0.9 }}
        style={{
          position: 'absolute',
          right: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          background: colors.glassBg,
          backdropFilter: 'blur(12px)',
          border: `1px solid ${colors.glassBorder}`,
          borderRadius: '50%',
          width: '52px',
          height: '52px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.gold,
          cursor: 'pointer',
          transition: 'all 0.3s',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}
        aria-label="Slide suivant"
      >
        <FaChevronRight size={22} />
      </motion.button>

      {/* Indicateurs */}
      <div
        style={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
        }}
      >
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            style={{
              width: '44px',
              height: '4px',
              borderRadius: '4px',
              background: idx === current ? colors.gold : 'rgba(255,255,255,0.15)',
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.3s',
              position: 'relative',
              overflow: 'hidden',
              padding: 0,
            }}
            aria-label={`Aller à la slide ${idx + 1}`}
          >
            {idx === current && (
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  background: `linear-gradient(90deg, ${colors.gold}, ${colors.goldHover})`,
                  borderRadius: '4px',
                }}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 6, ease: 'linear', repeat: Infinity, repeatDelay: 0 }}
              />
            )}
          </button>
        ))}
        <span
          style={{
            color: colors.textMuted,
            fontSize: '0.85rem',
            fontWeight: 300,
            letterSpacing: '0.05em',
            marginLeft: '8px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {current + 1} / {slides.length}
        </span>
      </div>
    </div>
  );
};

export default Banner;